import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { CheckCircle2, XCircle, FileText, BookText } from 'lucide-react';
import type { UserDetail } from '../types';

interface ActivityItem {
  type: 'extraction' | 'smart_note';
  id: string;
  created_at: string;
  success: boolean;
  title: string;
  meta?: string;
}

export function ActivityPanel({ detail }: { detail: UserDetail }) {
  const items: ActivityItem[] = [
    ...detail.extractions.map((e: any) => ({
      type: 'extraction' as const,
      id: e.id,
      created_at: e.created_at,
      success: e.extraction_success !== false,
      title: e.sender_email || 'Signature extraction',
      meta:
        e.fields_extracted != null
          ? `${e.fields_extracted}/${e.fields_requested ?? '?'} fields`
          : e.error_message || undefined,
    })),
    ...detail.smart_notes.map((n: any) => ({
      type: 'smart_note' as const,
      id: n.id,
      created_at: n.created_at,
      success: n.success !== false,
      title: n.company_name || 'Smart note',
      meta: n.error_message || undefined,
    })),
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-border/60 bg-card p-12 text-center">
        <p className="text-sm text-muted-foreground">No activity yet.</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
      <div className="p-4 border-b border-border/60">
        <h4 className="text-sm font-semibold">
          Recent activity <span className="text-muted-foreground font-normal">({items.length})</span>
        </h4>
      </div>
      <ul className="divide-y divide-border/60">
        {items.map((item) => (
          <li
            key={`${item.type}-${item.id}`}
            className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors"
          >
            <div
              className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                item.type === 'extraction' ? 'bg-primary/10 text-primary' : 'bg-warning/10 text-warning'
              }`}
            >
              {item.type === 'extraction' ? (
                <FileText className="h-4 w-4" />
              ) : (
                <BookText className="h-4 w-4" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium truncate">{item.title}</span>
                <Badge variant="outline" className="text-xs">
                  {item.type === 'extraction' ? 'Extraction' : 'Smart Note'}
                </Badge>
                {item.success ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-success" />
                ) : (
                  <XCircle className="h-3.5 w-3.5 text-destructive" />
                )}
              </div>
              {item.meta && (
                <div className="text-xs text-muted-foreground mt-0.5 truncate">{item.meta}</div>
              )}
            </div>
            <div className="text-xs text-muted-foreground whitespace-nowrap">
              {format(new Date(item.created_at), 'dd.MM HH:mm')}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
